import { useState } from 'react'

import { Divider, Switch, TextInput } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconClose from 'src/icons/IconClose'
import PlacesAutocomplete from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'
import SettingsField from 'src/pages/SettingsPage/components/SettingsField'

interface AddCompanyFormProps {
  onSave: (values: any) => void
  saveLoading?: boolean
}

const AddCompanyForm = ({
  onSave,
  saveLoading = false,
}: AddCompanyFormProps) => {
  const [formState, setFormState] = useState({
    companyName: '',
    dotNumber: '',
    einNumber: '',
    twilioAuthToken: '',
    accountId: '',
    phoneNumber: '',
    companyPhoneNumber: '',
    companyFaxNumber: '',
    address: {
      addressId: '',
      street: '',
      city: '',
      state: '',
      country: 'USA',
      zipCode: '',
    },
    useRecruiterLine: true,
    callRecordingsEnabled: true,
    transcriptionEnabled: true,
    voiceAgentEnabled: false,
    atsEnabled: true,
    recruiterLeadEmailNotifyEnabled: false,
    trainingEnabled: false,
    trainixCompanyId: '',
    preAdverseActionDelayHours: 24,
    finalAdverseActionDelayHours: 120,
  })

  const [hasChanges, setHasChanges] = useState(false)

  const handleInputChange = (
    field: string,
    value: string | boolean | number
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
    setHasChanges(true)
  }

  const handleAddressChange = (addressData) => {
    setFormState((prev) => ({
      ...prev,
      address: {
        addressId: addressData.addressId || '',
        street: addressData.street || '',
        city: addressData.city || '',
        state: addressData.state || '',
        country: addressData.country || 'USA',
        zipCode: addressData.zipCode || '',
      },
    }))
    setHasChanges(true)
  }

  const handleSave = () => {
    const { address: addressWithId, ...rest } = formState
    const { addressId: _addressId, ...address } = addressWithId

    onSave({ ...rest, address })
  }

  const isSaveDisabled =
    !formState.companyName ||
    !formState.dotNumber ||
    !formState.einNumber ||
    !formState.twilioAuthToken ||
    !formState.accountId ||
    !formState.phoneNumber

  const isAddressValid =
    formState.address.street &&
    formState.address.city &&
    formState.address.state &&
    formState.address.country &&
    formState.address.zipCode

  return (
    <div className="flex h-full flex-col gap-4 pb-6">
      <div className="flex items-center justify-between">
        <div className="text-base font-medium">Add Company</div>
        <DNButton
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaveDisabled}
          loading={saveLoading}
          className="w-[140px]"
        >
          Create Company
        </DNButton>
      </div>
      <Divider />

      <SettingsField
        title="Company Name"
        description="The legal name of the company"
        required
      >
        <TextInput
          className="w-[300px]"
          value={formState.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          placeholder="Enter company name"
        />
      </SettingsField>

      <SettingsField
        title="Company Address"
        description="Primary business address"
      >
        <PlacesAutocomplete
          value={formState.address}
          handleChange={handleAddressChange}
          placeholder="Start typing an address..."
          className="w-[300px]"
          rightSection={
            isAddressValid ? (
              <IconCheckmark className="h-3 w-3 fill-none stroke-green-600" />
            ) : (
              <IconClose className="h-2 w-2 fill-none stroke-red-700" />
            )
          }
        />
      </SettingsField>

      <SettingsField
        title="DOT Number"
        description="Department of Transportation number"
        required
      >
        <TextInput
          value={formState.dotNumber}
          onChange={(e) => handleInputChange('dotNumber', e.target.value)}
          placeholder="Enter DOT number"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="EIN Number"
        description="Employer Identification Number"
        required
      >
        <TextInput
          value={formState.einNumber}
          onChange={(e) => handleInputChange('einNumber', e.target.value)}
          placeholder="Enter EIN number"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Twilio Auth Token"
        description="Authentication token for Twilio integration"
        required
      >
        <TextInput
          type="password"
          value={formState.twilioAuthToken}
          onChange={(e) => handleInputChange('twilioAuthToken', e.target.value)}
          placeholder="Enter Twilio auth token"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Twilio Account ID"
        description="Account SID for Twilio integration"
        required
      >
        <TextInput
          value={formState.accountId}
          onChange={(e) => handleInputChange('accountId', e.target.value)}
          placeholder="Enter Twilio account SID"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Twilio Phone Number"
        description="Phone number for Twilio calls and SMS"
        required
      >
        <PhoneInput
          value={formState.phoneNumber}
          onChange={(value) => handleInputChange('phoneNumber', value)}
          placeholder="Enter Twilio phone number"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Company Phone Number"
        description="Main phone number for the company"
      >
        <PhoneInput
          value={formState.companyPhoneNumber}
          onChange={(value) => handleInputChange('companyPhoneNumber', value)}
          placeholder="Enter phone number"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Company Fax Number"
        description="Fax number for the company"
      >
        <PhoneInput
          value={formState.companyFaxNumber}
          onChange={(value) => handleInputChange('companyFaxNumber', value)}
          placeholder="Enter fax number"
          className="w-[300px]"
        />
      </SettingsField>

      <div className="mt-6 text-base font-medium">Configuration</div>
      <Divider />

      <SettingsField
        title="Use Recruiter Line"
        description="Enable dedicated recruiter phone line"
      >
        <Switch
          checked={formState.useRecruiterLine ?? false}
          onChange={(event) =>
            handleInputChange('useRecruiterLine', event.currentTarget.checked)
          }
        />
      </SettingsField>

      <SettingsField
        title="Call Recordings"
        description="Record phone calls for quality assurance"
      >
        <Switch
          checked={formState.callRecordingsEnabled ?? false}
          onChange={(event) =>
            handleInputChange(
              'callRecordingsEnabled',
              event.currentTarget.checked
            )
          }
        />
      </SettingsField>

      <SettingsField
        title="Transcription"
        description="Automatically transcribe call recordings"
      >
        <Switch
          checked={formState.transcriptionEnabled ?? false}
          onChange={(event) =>
            handleInputChange(
              'transcriptionEnabled',
              event.currentTarget.checked
            )
          }
        />
      </SettingsField>

      <SettingsField
        title="Voice Agent"
        description="AI-powered voice assistant for calls"
      >
        <Switch
          checked={formState.voiceAgentEnabled ?? false}
          onChange={(event) =>
            handleInputChange('voiceAgentEnabled', event.currentTarget.checked)
          }
        />
      </SettingsField>

      <SettingsField
        title="ATS Integration"
        description="Applicant Tracking System integration"
      >
        <Switch
          checked={formState.atsEnabled ?? false}
          onChange={(event) =>
            handleInputChange('atsEnabled', event.currentTarget.checked)
          }
        />
      </SettingsField>

      <SettingsField
        title="Recruiter Lead Email Notifications"
        description="Email notifications for new recruiter leads"
      >
        <Switch
          checked={formState.recruiterLeadEmailNotifyEnabled ?? false}
          onChange={(event) =>
            handleInputChange(
              'recruiterLeadEmailNotifyEnabled',
              event.currentTarget.checked
            )
          }
        />
      </SettingsField>

      <SettingsField
        title="Training Mode"
        description="Enable training features and tools"
      >
        <Switch
          checked={formState.trainingEnabled ?? false}
          onChange={(event) =>
            handleInputChange('trainingEnabled', event.currentTarget.checked)
          }
        />
      </SettingsField>

      <SettingsField
        title="Trainix Company ID"
        description="Integration ID for Trainix platform"
      >
        <TextInput
          value={formState.trainixCompanyId}
          onChange={(e) =>
            handleInputChange('trainixCompanyId', e.target.value)
          }
          placeholder="Enter Trainix Company ID"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Pre-Adverse Action Delay"
        description="Delay in hours before sending pre-adverse action notice"
      >
        <TextInput
          value={formState.preAdverseActionDelayHours}
          onChange={(e) => {
            if (e.target.value === '') {
              handleInputChange('preAdverseActionDelayHours', 0)
            } else {
              const value = parseInt(e.target.value)
              handleInputChange('preAdverseActionDelayHours', value)
            }
          }}
          placeholder="hours"
          className="w-[300px]"
        />
      </SettingsField>

      <SettingsField
        title="Final Adverse Action Delay"
        description="Delay in hours before sending final adverse action notice"
      >
        <TextInput
          className="w-[300px]"
          value={formState.finalAdverseActionDelayHours}
          onChange={(e) => {
            if (e.target.value === '') {
              handleInputChange('finalAdverseActionDelayHours', 0)
            } else {
              const value = parseInt(e.target.value)
              handleInputChange('finalAdverseActionDelayHours', value)
            }
          }}
          placeholder="hours"
        />
      </SettingsField>
    </div>
  )
}

export default AddCompanyForm
